/**
 * MessagesTab.js - Enhanced Messaging center for Owner Dashboard
 * Categories: Customers, Workers, Support
 * Actions: Reply, Archive, Edit, Delete
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Users, Plus, CheckCircle, Clock, Send, X, Search,
  User, Archive, Edit, Trash2, Reply, MoreVertical, Phone, Mail,
  AlertCircle, MessageCircle, Briefcase, HeartHandshake, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api, { messagingAPI } from '../../services/api';

const MESSAGE_CATEGORIES = [
  { id: 'customers', label: 'Customers', icon: User, color: 'blue' },
  { id: 'workers', label: 'Workers', icon: Briefcase, color: 'green' },
  { id: 'support', label: 'Support', icon: HeartHandshake, color: 'purple' }
];

const MessagesTab = () => {
  // Category and filter state
  const [activeCategory, setActiveCategory] = useState('customers');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Conversations by category
  const [customerConversations, setCustomerConversations] = useState([]);
  const [workerConversations, setWorkerConversations] = useState([]);
  const [supportConversations, setSupportConversations] = useState([]);
  const [archivedConversations, setArchivedConversations] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [showConversationModal, setShowConversationModal] = useState(false);
  
  // New message modal
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [newMessageRecipientType, setNewMessageRecipientType] = useState('customer');
  const [recipients, setRecipients] = useState({ workers: [], customers: [] });
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  
  // Message actions
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [showActions, setShowActions] = useState(null);
  
  const messagesEndRef = useRef(null);

  // Fetch all conversations
  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const [supportRes, bookingRes, directRes] = await Promise.all([
        messagingAPI.getSupportConversations().catch(() => ({ data: [] })),
        messagingAPI.getBookingConversations().catch(() => ({ data: [] })),
        messagingAPI.getDirectConversations().catch(() => ({ data: [] }))
      ]);
      
      // Normalize participant names across different conversation types
      const normalizeConversation = (conv, category) => {
        // Get participant name from various possible fields
        const participantName = conv.user_name || conv.recipient_name || conv.customer_name || 
          conv.participant_names?.[Object.keys(conv.participant_names || {}).find(k => k !== conv.owner_id)] ||
          'Unknown';
        
        return {
          ...conv,
          category,
          participant_name: participantName,
          participant_type: conv.user_role || conv.recipient_role || 'customer'
        };
      };
      
      const allConversations = [
        ...(supportRes.data || []).map(c => normalizeConversation(c, 'support')),
        ...(bookingRes.data || []).map(c => normalizeConversation(c, c.participant_type === 'worker' ? 'workers' : 'customers')),
        ...(directRes.data || []).map(c => normalizeConversation(c, c.recipient_role === 'worker' ? 'workers' : 'customers'))
      ];
      
      // Separate by category and archived status
      const active = allConversations.filter(c => c.status !== 'archived');
      const archived = allConversations.filter(c => c.status === 'archived');
      
      setCustomerConversations(active.filter(c => c.category === 'customers'));
      setWorkerConversations(active.filter(c => c.category === 'workers'));
      setSupportConversations(active.filter(c => c.category === 'support'));
      setArchivedConversations(archived);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch recipients for new message (workers and users who have booked)
  const fetchRecipients = useCallback(async () => {
    try {
      const [workersRes, usersRes] = await Promise.all([
        api.get('/workers').catch(() => ({ data: [] })),
        messagingAPI.getAllUsers().catch(() => ({ data: [] }))
      ]);
      
      // Filter users by role - customers are users who are not workers or owners
      const allUsers = usersRes.data || [];
      const customers = allUsers.filter(u => u.role === 'customer');
      
      setRecipients({
        workers: workersRes.data || [],
        customers: customers
      });
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchRecipients();
  }, [fetchConversations, fetchRecipients]);

  // Get conversations for current category
  const getCurrentConversations = () => {
    let convs = [];
    switch (activeCategory) {
      case 'customers':
        convs = customerConversations;
        break;
      case 'workers':
        convs = workerConversations;
        break;
      case 'support':
        convs = supportConversations;
        break;
      case 'archived':
        convs = archivedConversations;
        break;
      default:
        convs = [...customerConversations, ...workerConversations, ...supportConversations];
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      convs = convs.filter(c => c.status === statusFilter);
    }
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      convs = convs.filter(c => 
        c.participant_name?.toLowerCase().includes(query) ||
        c.subject?.toLowerCase().includes(query) ||
        c.last_message?.toLowerCase().includes(query)
      );
    }
    
    return convs.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
  };

  // Open conversation and fetch messages
  const openConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setShowConversationModal(true);
    setConversationMessages([]);
    
    try {
      const res = await messagingAPI.getMessages(conversation.id);
      setConversationMessages(res.data || []);
      
      // Mark as read
      if (conversation.unread_count > 0) {
        await messagingAPI.markAsRead(conversation.id);
        fetchConversations();
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationMessages]);

  // Send reply message
  const sendReply = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await messagingAPI.sendMessage(selectedConversation.id, newMessage);
      setNewMessage('');
      
      // Refresh messages
      const res = await messagingAPI.getMessages(selectedConversation.id);
      setConversationMessages(res.data || []);
      fetchConversations();
      toast.success('Message sent!');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Archive conversation
  const archiveConversation = async (convId) => {
    try {
      await api.put(`/messaging/conversations/${convId}/status`, null, {
        params: { status: 'archived' }
      });
      toast.success('Conversation archived');
      fetchConversations();
      if (selectedConversation?.id === convId) {
        setShowConversationModal(false);
        setSelectedConversation(null);
      }
    } catch (error) {
      toast.error('Failed to archive conversation');
    }
  };

  // Unarchive conversation
  const unarchiveConversation = async (convId) => {
    try {
      await api.put(`/messaging/conversations/${convId}/status`, null, {
        params: { status: 'open' }
      });
      toast.success('Conversation restored');
      fetchConversations();
    } catch (error) {
      toast.error('Failed to restore conversation');
    }
  };

  // Delete conversation
  const deleteConversation = async (convId) => {
    if (!window.confirm('Delete this conversation permanently?')) return;
    
    try {
      await api.delete(`/messaging/conversations/${convId}`);
      toast.success('Conversation deleted');
      fetchConversations();
      if (selectedConversation?.id === convId) {
        setShowConversationModal(false);
        setSelectedConversation(null);
      }
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  // Edit message
  const startEditMessage = (message) => {
    setEditingMessage(message);
    setEditText(message.content);
    setShowActions(null);
  };

  const saveEditMessage = async () => {
    if (!editingMessage || !editText.trim()) return;
    
    try {
      await api.put(`/messaging/messages/${editingMessage.id}`, {
        content: editText
      });
      toast.success('Message updated');
      setEditingMessage(null);
      setEditText('');
      
      // Refresh messages
      const res = await messagingAPI.getMessages(selectedConversation.id);
      setConversationMessages(res.data || []);
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  // Delete message
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      await api.delete(`/messaging/messages/${messageId}`);
      toast.success('Message deleted');
      setShowActions(null);
      
      // Refresh messages
      const res = await messagingAPI.getMessages(selectedConversation.id);
      setConversationMessages(res.data || []);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  // Start new conversation
  const startNewConversation = async () => {
    if (!selectedRecipient || !newMessage.trim()) {
      toast.error('Please select a recipient and enter a message');
      return;
    }
    
    setSending(true);
    try {
      await messagingAPI.createConversation({
        type: 'direct',
        participant_id: selectedRecipient.id,
        participant_type: newMessageRecipientType,
        initial_message: newMessage
      });
      
      toast.success('Message sent!');
      setShowNewMessageModal(false);
      setNewMessage('');
      setSelectedRecipient(null);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to start conversation');
    } finally {
      setSending(false);
    }
  };

  // Get category counts
  const getCounts = () => ({
    customers: customerConversations.length,
    workers: workerConversations.length,
    support: supportConversations.length,
    archived: archivedConversations.length,
    unread: [...customerConversations, ...workerConversations, ...supportConversations]
      .reduce((sum, c) => sum + (c.unread_count || 0), 0)
  });

  const counts = getCounts();
  const filteredConversations = getCurrentConversations();

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[var(--primary)]" />
            Messages
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            {counts.unread > 0 ? `${counts.unread} unread message${counts.unread > 1 ? 's' : ''}` : 'Manage all conversations'}
          </p>
        </div>
        <Button onClick={() => setShowNewMessageModal(true)} className="btn-primary" data-testid="new-message-btn">
          <Plus className="w-4 h-4 mr-2" /> New Message
        </Button>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-3">
        {MESSAGE_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => { setActiveCategory(cat.id); setStatusFilter('all'); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeCategory === cat.id 
                ? `bg-${cat.color}-100 text-${cat.color}-700 border-2 border-${cat.color}-300` 
                : 'bg-[var(--background-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'
            }`}
            data-testid={`category-${cat.id}`}
          >
            <cat.icon className="w-4 h-4" />
            {cat.label}
            {counts[cat.id] > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeCategory === cat.id ? `bg-${cat.color}-200` : 'bg-[var(--background-subtle)]'
              }`}>
                {counts[cat.id]}
              </span>
            )}
          </button>
        ))}
        <button
          onClick={() => { setActiveCategory('archived'); setStatusFilter('all'); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeCategory === 'archived' 
              ? 'bg-[var(--background-subtle)] text-[var(--text-primary)] border-2 border-gray-400' 
              : 'bg-[var(--background-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'
          }`}
          data-testid="category-archived"
        >
          <Archive className="w-4 h-4" />
          Archived
          {counts.archived > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-[var(--background-subtle)]">{counts.archived}</span>
          )}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'open', 'in_progress', 'resolved'].map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' ? 'All' : status === 'in_progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Conversations List */}
      <div className="space-y-3">
        {filteredConversations.length === 0 ? (
          <div className="card p-12 text-center">
            <MessageSquare className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">No Conversations</h3>
            <p className="text-[var(--text-muted)] mb-4">
              {activeCategory === 'archived' 
                ? 'No archived conversations' 
                : 'Start a new conversation to get started'}
            </p>
            {activeCategory !== 'archived' && (
              <Button onClick={() => setShowNewMessageModal(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" /> New Message
              </Button>
            )}
          </div>
        ) : (
          filteredConversations.map((conv) => (
            <motion.div
              key={conv.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`card p-4 cursor-pointer hover:shadow-md transition-shadow ${
                conv.unread_count > 0 ? 'border-l-4 border-l-[var(--primary)] bg-[#D4AF37]/10/50' : ''
              }`}
              data-testid={`conversation-${conv.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                  conv.category === 'customers' ? 'bg-[#D4AF37]/100' :
                  conv.category === 'workers' ? 'bg-green-500' : 'bg-pink-300'
                }`}>
                  {conv.participant_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0" onClick={() => openConversation(conv)}>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold truncate">{conv.participant_name || 'Unknown'}</h3>
                    {conv.unread_count > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-[var(--primary)] text-white">
                        {conv.unread_count} new
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      conv.status === 'open' ? 'bg-[var(--success)]/15 text-green-700' :
                      conv.status === 'in_progress' ? 'bg-[var(--warning)]/15 text-yellow-700' :
                      conv.status === 'resolved' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                      'bg-[var(--background-subtle)] text-[var(--text-secondary)]'
                    }`}>
                      {conv.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--text-muted)] truncate">{conv.last_message || 'No messages yet'}</p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {new Date(conv.updated_at || conv.created_at).toLocaleString()}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); openConversation(conv); }}
                    title="Reply"
                  >
                    <Reply className="w-4 h-4" />
                  </Button>
                  {activeCategory === 'archived' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[var(--success)]"
                      onClick={(e) => { e.stopPropagation(); unarchiveConversation(conv.id); }}
                      title="Restore"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[var(--warning)]"
                      onClick={(e) => { e.stopPropagation(); archiveConversation(conv.id); }}
                      title="Archive"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[var(--error)]"
                    onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Conversation Modal */}
      <AnimatePresence>
        {showConversationModal && selectedConversation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl w-full max-w-2xl h-[80vh] flex flex-col"
            >
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    selectedConversation.category === 'customers' ? 'bg-[#D4AF37]/100' :
                    selectedConversation.category === 'workers' ? 'bg-green-500' : 'bg-pink-300'
                  }`}>
                    {selectedConversation.participant_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="font-bold">{selectedConversation.participant_name}</h3>
                    <p className="text-xs text-[var(--text-muted)]">{selectedConversation.participant_email || selectedConversation.participant_phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[var(--warning)]"
                    onClick={() => archiveConversation(selectedConversation.id)}
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[var(--error)]"
                    onClick={() => deleteConversation(selectedConversation.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowConversationModal(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[var(--background-subtle)]">
                {conversationMessages.length === 0 ? (
                  <div className="text-center text-[var(--text-muted)] py-8">
                    No messages yet. Send the first message!
                  </div>
                ) : (
                  conversationMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === selectedConversation.owner_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2 relative group ${
                          msg.sender_id === selectedConversation.owner_id
                            ? 'bg-[var(--primary)] text-white rounded-br-md'
                            : 'bg-[var(--background-paper)] border rounded-bl-md'
                        }`}
                      >
                        {editingMessage?.id === msg.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              rows={2}
                              className="text-[var(--text-primary)]"
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={saveEditMessage}>Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingMessage(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender_id === selectedConversation.owner_id ? 'text-white/70' : 'text-[var(--text-muted)]'
                            }`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {msg.edited_at && ' (edited)'}
                            </p>
                            
                            {/* Message actions (owner's messages only) */}
                            {msg.sender_id === selectedConversation.owner_id && (
                              <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="flex gap-1 bg-[var(--background-paper)] rounded-lg shadow-lg p-1">
                                  <button
                                    onClick={() => startEditMessage(msg)}
                                    className="p-1 hover:bg-[var(--background-subtle)] rounded"
                                    title="Edit"
                                  >
                                    <Edit className="w-3 h-3 text-[var(--text-secondary)]" />
                                  </button>
                                  <button
                                    onClick={() => deleteMessage(msg.id)}
                                    className="p-1 hover:bg-[var(--background-subtle)] rounded"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-3 h-3 text-[var(--error)]" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t bg-[var(--background-paper)]">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your reply..."
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                    className="flex-1"
                    data-testid="reply-input"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sending || !newMessage.trim()}
                    className="btn-primary"
                    data-testid="send-reply-btn"
                  >
                    {sending ? '...' : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* New Message Modal */}
      <AnimatePresence>
        {showNewMessageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl w-full max-w-lg"
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-bold">New Message</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowNewMessageModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 space-y-4">
                {/* Recipient Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Send To</label>
                  <div className="flex gap-2">
                    <Button
                      variant={newMessageRecipientType === 'customer' ? 'default' : 'outline'}
                      onClick={() => { setNewMessageRecipientType('customer'); setSelectedRecipient(null); }}
                    >
                      <User className="w-4 h-4 mr-2" /> Customer
                    </Button>
                    <Button
                      variant={newMessageRecipientType === 'worker' ? 'default' : 'outline'}
                      onClick={() => { setNewMessageRecipientType('worker'); setSelectedRecipient(null); }}
                    >
                      <Briefcase className="w-4 h-4 mr-2" /> Worker
                    </Button>
                  </div>
                </div>

                {/* Recipient Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">Select Recipient</label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg">
                    {(newMessageRecipientType === 'customer' ? recipients.customers : recipients.workers).map(recipient => (
                      <button
                        key={recipient.id}
                        onClick={() => setSelectedRecipient(recipient)}
                        className={`w-full p-3 text-left border-b last:border-0 flex items-center gap-3 hover:bg-[var(--background-subtle)] ${
                          selectedRecipient?.id === recipient.id ? 'bg-[#D4AF37]/10' : ''
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-[var(--background-subtle)] flex items-center justify-center">
                          {recipient.full_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium">{recipient.full_name}</p>
                          <p className="text-xs text-[var(--text-muted)]">{recipient.email}</p>
                        </div>
                        {selectedRecipient?.id === recipient.id && (
                          <CheckCircle className="w-5 h-5 text-[var(--success)] ml-auto" />
                        )}
                      </button>
                    ))}
                    {(newMessageRecipientType === 'customer' ? recipients.customers : recipients.workers).length === 0 && (
                      <p className="p-4 text-center text-[var(--text-muted)]">
                        No {newMessageRecipientType}s found
                      </p>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={4}
                  />
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewMessageModal(false)}>Cancel</Button>
                <Button
                  onClick={startNewConversation}
                  disabled={sending || !selectedRecipient || !newMessage.trim()}
                  className="btn-primary"
                >
                  {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessagesTab;
